import React, { useEffect, useState } from 'react';

/**
 * @description Um componente reutilizável que exibe uma animação de confetes e balões para celebrar uma ação.
 * @param {object} props
 * @param {boolean} props.show - Controla a ativação da animação.
 * @param {function} props.onAnimationEnd - Callback executado quando a animação termina para resetar o estado no componente pai.
 */
const CelebrationAnimation = ({ show, onAnimationEnd }) => {
    const [isVisible, setIsVisible] = useState(false);

    // Controla a visibilidade e a duração da animação
    useEffect(() => {
        if (show) {
            setIsVisible(true);
            // Define um timer para esconder o componente e chamar o callback de finalização
            const timer = setTimeout(() => {
                setIsVisible(false);
                if (onAnimationEnd) {
                    onAnimationEnd();
                }
            }, 5000); // A animação dura 5 segundos no total
            return () => clearTimeout(timer);
        }
    }, [show, onAnimationEnd]);

    if (!isVisible) {
        return null;
    }

    const confettiCount = 150;
    const balloonCount = 15;
    const colors = ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50', '#8bc34a', '#ffeb3b', '#ffc107', '#ff9800'];

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            zIndex: 9999,
            pointerEvents: 'none',
        }}>
            <style>
                {`
                @keyframes confetti-fall {
                    0% { transform: translateY(-10vh) rotate(0deg) rotateY(0deg); opacity: 1; }
                    100% { transform: translateY(110vh) rotate(1080deg) rotateY(360deg); opacity: 0; }
                }
                @keyframes balloon-rise {
                    0% { transform: translateY(110vh) rotate(-10deg); opacity: 0.9; }
                    100% { transform: translateY(-20vh) rotate(10deg); opacity: 0; }
                }
                .confetti {
                    position: absolute;
                    width: 8px;
                    height: 16px;
                    animation-name: confetti-fall;
                    animation-timing-function: linear;
                    animation-iteration-count: 1;
                }
                .balloon {
                    position: absolute;
                    width: 80px;
                    height: 100px;
                    border-radius: 50%;
                    animation-name: balloon-rise;
                    animation-timing-function: ease-out;
                    animation-iteration-count: 1;
                    background-color: var(--balloon-color);
                }
                .balloon::after {
                    content: '';
                    position: absolute;
                    bottom: -8px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 6px solid transparent;
                    border-right: 6px solid transparent;
                    border-top: 12px solid var(--balloon-color);
                }
                `}
            </style>
            {/* Renderiza os confetes com valores aleatórios */}
            {Array.from({ length: confettiCount }).map((_, i) => {
                const style = {
                    left: `${Math.random() * 100}vw`,
                    backgroundColor: colors[Math.floor(Math.random() * colors.length)],
                    animationDuration: `${Math.random() * 3 + 2}s`, // 2s a 5s
                    animationDelay: `${Math.random() * 1}s`,
                };
                return <div key={`confetti-${i}`} className="confetti" style={style}></div>;
            })}
            {/* Renderiza os balões com valores aleatórios */}
            {Array.from({ length: balloonCount }).map((_, i) => {
                const color = colors[Math.floor(Math.random() * colors.length)];
                const style = {
                    left: `${Math.random() * 90}vw`, // Evita que saiam da tela
                    animationDuration: `${Math.random() * 2 + 3}s`, // 3s a 5s
                    animationDelay: `${Math.random() * 2}s`,
                    '--balloon-color': color, // CSS variable para a cor do balão e seu "nó"
                };
                return (
                    <div key={`balloon-${i}`} className="balloon" style={style}></div>
                );
            })}
        </div>
    );
};

export default CelebrationAnimation;
